import { isDebugEnabled } from './utils/debug';

type EventCallback = (payload?: any) => void;

type Listener = {
  event: string;
  fn: EventCallback;
  once: boolean;
};

interface OutgoingMessage {
  v: number;
  type: string;
  payload?: unknown;
}

interface IncomingMessage {
  v?: number;
  type: string;
  seq?: number;
  room?: string;
  payload?: unknown;
}

const WS_URL = buildWsUrl(import.meta.env.VITE_WS_URL || 'http://localhost:3001');
const PROTOCOL_VERSION = 1;

function buildWsUrl(input: string): string {
  let url = input.trim();
  if (!url) url = 'ws://localhost:3001';
  if (url.startsWith('http://')) url = 'ws://' + url.slice(7);
  else if (url.startsWith('https://')) url = 'wss://' + url.slice(8);
  else if (!url.startsWith('ws://') && !url.startsWith('wss://')) url = 'ws://' + url;
  if (!url.endsWith('/ws')) {
    url = url.replace(/\/$/, '') + '/ws';
  }
  return url;
}

class NativeSocket {
  private ws: WebSocket | null = null;
  private listeners: Listener[] = [];
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyDisconnected = false;
  private pendingRoomId: string | null = null;
  private pendingJoinPayload: unknown = null;
  private pendingQueue: OutgoingMessage[] = [];
  // Cache the most recent payload for each event type so late-registered listeners
  // (e.g. after a component mount) receive the current state immediately.
  // Keys are scoped to the room (for room events) to prevent stale cross-room state.
  private lastEvents: Map<string, unknown> = new Map();
  private currentRoomId: string | null = null;
  private _connected = false;
  private _connecting = false;

  // Emulate socket.io public API
  id = '';

  get connected() {
    return this._connected;
  }

  get connecting() {
    return this._connecting;
  }

  connect(): this {
    if (this.ws && (this._connected || this._connecting)) return this;
    this.intentionallyDisconnected = false;
    this._connecting = true;

    try {
      this.ws = new WebSocket(WS_URL);
    } catch (err) {
      this._connecting = false;
      this.scheduleReconnect();
      return this;
    }

    this.ws.onopen = () => {
      this._connecting = false;
      this._connected = true;
      this.reconnectAttempts = 0;
      this.id = this.ws?.url || '';
      // A fresh connection starts with no room state; clear cached room events
      // so stale data from a previous room/session is never delivered to listeners.
      this.clearRoomEventCache();
      this.emitToListeners('connect', undefined);
      // If we had a pending join from a reconnect, resend it first.
      if (this.pendingRoomId && this.pendingJoinPayload) {
        this.sendRaw({ v: PROTOCOL_VERSION, type: 'join', payload: this.pendingJoinPayload });
      }
      // Flush any messages that were emitted before the socket opened.
      const queue = this.pendingQueue;
      this.pendingQueue = [];
      for (const msg of queue) {
        this.sendRaw(msg);
      }
    };

    this.ws.onmessage = (event) => {
      let msg: IncomingMessage;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (isDebugEnabled('ws') || isDebugEnabled('all')) {
        console.log('[socket] incoming', msg.type, msg);
      }
      if (msg.type === 'ping') {
        this.sendRaw({ v: PROTOCOL_VERSION, type: 'pong' });
        return;
      }
      if (msg.type === 'pong') return;
      this.dispatch(msg.type, msg.payload, msg.room);
    };

    this.ws.onclose = () => {
      this._connected = false;
      this._connecting = false;
      this.emitToListeners('disconnect', undefined);
      if (!this.intentionallyDisconnected) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // Let onclose handle reconnection logic.
      this._connecting = false;
    };

    return this;
  }

  disconnect(): this {
    this.intentionallyDisconnected = true;
    this.clearReconnectTimer();
    this.clearRoomEventCache();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this._connected = false;
    this._connecting = false;
    return this;
  }

  on(event: string, fn: EventCallback): this {
    this.listeners.push({ event, fn, once: false });
    // If we already have a cached payload for this event, deliver it immediately
    // so late-mounted components (e.g. RoomPage after RoomJoinGate) start with
    // the latest state instead of waiting for the next broadcast.
    // Room-scoped events are keyed by current room so stale cross-room state is
    // never delivered after navigation.
    const roomAwareKey = this.currentRoomId ? this.cacheKey(event, this.currentRoomId) : event;
    const last = this.lastEvents.get(roomAwareKey) ?? this.lastEvents.get(event);
    if (last !== undefined) {
      try {
        fn(last);
      } catch (err) {
        console.error(`[socket] cached listener error for ${event}`, err);
      }
    }
    return this;
  }

  off(event: string, fn: EventCallback): this {
    this.listeners = this.listeners.filter((l) => !(l.event === event && l.fn === fn));
    return this;
  }

  once(event: string, fn: EventCallback): this {
    this.listeners.push({ event, fn, once: true });
    return this;
  }

  /**
   * Set the room context for this socket without sending anything. Clears cached
   * room events so stale state from a previous room is never delivered to listeners
   * registered after this call.
   */
  setRoomContext(roomId: string | null): this {
    if (roomId !== this.currentRoomId) {
      this.clearRoomEventCache();
      this.currentRoomId = roomId;
      if (roomId) {
        this.pendingRoomId = roomId;
      } else {
        this.pendingRoomId = null;
        this.pendingJoinPayload = null;
      }
    }
    return this;
  }

  emit(type: string, payload?: unknown): this {
    // Compatibility aliases for event names used by the rest of the frontend.
    const gatewayType = mapOutgoingEvent(type);

    if (type === 'join' || type === 'join_room') {
      const roomId = (payload as any)?.roomId || null;
      if (roomId && roomId !== this.currentRoomId) {
        // Changing rooms: clear cached room events so the next room does not
        // receive stale state from the previous one.
        this.clearRoomEventCache();
        this.currentRoomId = roomId;
      }
      this.pendingRoomId = roomId;
      this.pendingJoinPayload = payload;
    } else if (type === 'leave' || type === 'leave_room') {
      this.pendingRoomId = null;
      this.pendingJoinPayload = null;
      this.currentRoomId = null;
      this.clearRoomEventCache();
    }

    const msg: OutgoingMessage = { v: PROTOCOL_VERSION, type: gatewayType, payload };

    // If the socket isn't open yet, queue the message so it is sent as soon as
    // the connection is established. This prevents race conditions where the
    // frontend calls emit() immediately after connect().
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingQueue.push(msg);
      return this;
    }

    this.sendRaw(msg);
    return this;
  }

  private sendRaw(msg: OutgoingMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Queue not implemented; caller should ensure connect() was called.
      return;
    }
    try {
      this.ws.send(JSON.stringify(msg));
    } catch (err) {
      // ignore
    }
  }

  // Events that represent current room state. Caching these allows late-mounted
  // components to sync immediately instead of waiting for the next broadcast.
  // Room-scoped events are keyed by `${event}:${roomId}` to prevent stale
  // cross-room state from being delivered after navigation.
  private roomScopedEvents = new Set([
    'state',
    'room_joined',
    'queue_update',
    'moments_update',
    'blocked_users_update',
    'show_full_queue_update',
    'room_ended',
  ]);

  private cacheableEvents = new Set([
    ...this.roomScopedEvents,
    'active_rooms',
    'authenticate',
  ]);

  private shouldCacheEvent(event: string): boolean {
    return this.cacheableEvents.has(event);
  }

  private cacheKey(event: string, room?: string): string {
    if (room && this.roomScopedEvents.has(event)) {
      return `${event}:${room}`;
    }
    return event;
  }

  private clearRoomEventCache() {
    for (const key of this.lastEvents.keys()) {
      if (key.includes(':')) {
        this.lastEvents.delete(key);
      }
    }
  }

  private dispatch(type: string, payload?: unknown, roomId?: string) {
    // Map incoming gateway events to legacy socket.io event names for compatibility.
    const legacyType = mapIncomingEvent(type);
    // Some gateway payloads wrap data in an object; unwrap for legacy listeners.
    const normalizedPayload = normalizePayload(legacyType, payload);
    const resolvedRoomId = roomId || (payload as any)?.roomId || undefined;
    if (this.shouldCacheEvent(legacyType)) {
      this.lastEvents.set(this.cacheKey(legacyType, resolvedRoomId), normalizedPayload);
    }
    this.emitToListeners(legacyType, normalizedPayload);
    // Also emit under the raw gateway type for newer listeners.
    if (legacyType !== type) {
      if (this.shouldCacheEvent(type)) {
        this.lastEvents.set(this.cacheKey(type, resolvedRoomId), payload);
      }
      this.emitToListeners(type, payload);
    }
  }

  private emitToListeners(event: string, payload?: unknown) {
    const toCall = this.listeners.filter((l) => l.event === event);
    this.listeners = this.listeners.filter((l) => !(l.event === event && l.once));
    toCall.forEach((l) => {
      try {
        l.fn(payload);
      } catch (err) {
        console.error(`[socket] listener error for ${event}`, err);
      }
    });
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();
    const base = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);
    const jitter = Math.floor(Math.random() * 1000);
    const delay = base + jitter;
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

function mapOutgoingEvent(type: string): string {
  switch (type) {
    case 'join_room':
      return 'join';
    case 'leave_room':
      return 'leave';
    case 'send_reaction':
      return 'reaction';
    case 'search_youtube':
      return 'youtube_search';
    default:
      return type;
  }
}

function mapIncomingEvent(type: string): string {
  switch (type) {
    case 'queue_status':
      return 'queue_status_update';
    case 'reaction':
      return 'receive_reaction';
    case 'error':
      return 'queue_error';
    case 'youtube_results':
      return 'youtube_results';
    case 'recommendations_data':
      return 'recommendations_data';
    case 'curated_lists':
      return 'curated_lists';
    default:
      return type;
  }
}

function normalizePayload(type: string, payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload;
  switch (type) {
    case 'active_rooms':
      return (payload as any).rooms ?? payload;
    case 'queue_update':
      return (payload as any).queue !== undefined ? { queue: (payload as any).queue, seq: (payload as any).seq } : payload;
    case 'curated_lists':
      return (payload as any).lists ?? payload;
    case 'queue_status_update':
      return (payload as any).isOpen ?? payload;
    case 'max_messages_update':
      return (payload as any).maxMessages ?? payload;
    case 'countdown_update':
      return (payload as any).countdownSeconds ?? payload;
    case 'queue_error':
      return (payload as any).error ?? payload;
    case 'youtube_error':
      return (payload as any).error ?? payload;
    case 'reaction_summary':
      return (payload as any).summary ?? payload;
    default:
      return payload;
  }
}

export const socket = new NativeSocket();
